import { useEffect } from 'react';
import { Controller, useWatch } from 'react-hook-form';

import { Badge } from '@/components/ui/badge';
import { DocumentFieldMapping } from '@/services/types/letters-types';

import { EnhancedSelect } from '../shared/EnhancedSelect';
import type { BaseFieldProps, LoRScalarChoice } from '../types';

// Filter contacts based on selected adjuster (id match preferred, name fallback)
const filterContactsByAdjuster = (
  contacts: LoRScalarChoice[],
  selectedAdjuster?: LoRScalarChoice | string,
): LoRScalarChoice[] => {
  if (!selectedAdjuster || typeof selectedAdjuster === 'string') {
    // No adjuster selected or manual string entry – return all contacts
    return contacts;
  }

  return contacts.filter((contact) => {
    // Prefer matching by adjuster_id when present
    if (contact.context?.adjuster_id && selectedAdjuster.context?.adjuster_id) {
      return (
        contact.context.adjuster_id === selectedAdjuster.context.adjuster_id
      );
    }
    // Fallback to name match for legacy data
    return contact.context?.name === selectedAdjuster.value;
  });
};

/**
 * Custom trigger renderer for contact display
 */
const renderContactTrigger = (value: LoRScalarChoice | string) => {
  if (typeof value === 'string') {
    return <span className="font-medium text-gray-900">{value}</span>;
  }

  return (
    <span className="flex items-center gap-2">
      <span className="font-medium text-gray-900">{value.value}</span>
      {value.context?.contact_handle?.handle_type && (
        <Badge
          variant="secondary"
          className="text-xs px-1.5 py-0.5 bg-blue-50 border-blue-200 text-blue-700"
        >
          {value.context.contact_handle.handle_type.toLowerCase()}
        </Badge>
      )}
    </span>
  );
};

/**
 * Custom choice renderer for contact options
 */
const renderContactChoice = (choice: LoRScalarChoice) => (
  <div className="flex items-center justify-between w-full">
    <span className="font-medium text-gray-900">{choice.value}</span>
    {choice.context?.contact_handle?.handle_type && (
      <Badge
        variant="secondary"
        className="ml-2 text-xs px-2 py-0.5 bg-blue-50 border-blue-200 text-blue-700"
      >
        {choice.context.contact_handle.handle_type.toLowerCase()}
      </Badge>
    )}
  </div>
);

export const AdjusterContactField = ({
  field,
  control,
  name,
  required,
  error,
}: BaseFieldProps<typeof DocumentFieldMapping.ADJUSTER_CONTACT>) => {
  const choices = (field.default_value as LoRScalarChoice[]) || [];

  // Watch adjuster field (may be undefined if field not in template)
  const selectedAdjuster = useWatch({
    control,
    name: DocumentFieldMapping.ADJUSTER_NAME,
  }) as LoRScalarChoice | string | undefined;

  // Reset contact when adjuster selection changes (only if adjuster field exists)
  useEffect(() => {
    if (
      'setValue' in control &&
      selectedAdjuster !== undefined /* field exists */
    ) {
      (control as any).setValue(
        DocumentFieldMapping.ADJUSTER_CONTACT,
        undefined,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAdjuster]);

  // Guard against any non-fax/email handles leaking through
  const faxEmailOnly = choices.filter((c) => {
    const t = String(
      c.context?.contact_handle?.handle_type || '',
    ).toUpperCase();
    return t.includes('FAX') || t.includes('EMAIL');
  });
  const availableChoices = filterContactsByAdjuster(
    faxEmailOnly,
    selectedAdjuster,
  );

  return (
    <Controller
      control={control}
      name={name}
      rules={{
        required: required ? 'Please select or enter a contact' : false,
      }}
      render={({ field: ctrlField }) => (
        <EnhancedSelect
          value={ctrlField.value}
          onChange={ctrlField.onChange}
          onBlur={ctrlField.onBlur}
          name={ctrlField.name}
          choices={availableChoices}
          placeholder="Choose contact..."
          fieldDisplayName="contact"
          error={error}
          renderTriggerContent={renderContactTrigger}
          renderChoiceContent={renderContactChoice}
          emptyStateMessage="No contacts found"
          emptyStateDescription="Contact options will appear when relevant data is available."
        />
      )}
    />
  );
};
